/**
 * CSV template generator for bulk tile creation
 * Generates CSV templates from device points (with examples) or empty templates
 */
import { BacnetPoint, BacnetPointType } from '../../../../shared/models/point';
import { DeviceId, DeviceType } from '../../../../shared/models/device';
import { ModbusPointID, ModbusPoint, ModbusDataType } from '../../../../shared/models/modbus';
import { LoRaWANPoint, LoRaWANPointFormat } from '../../../../shared/models/lorawan';
import { TileFormat } from '../../../../shared/models/tile';
import * as Papa from 'papaparse';
import { PROTOCOL_CSV_CONFIG, CsvFieldId } from '../../shared/constants/csv-columns.constants';
import { IconGuesserUtil } from '../../../../shared/utils/icon-guesser.util';
import { BULK_CREATION_PROTOCOL_HANDLERS } from './bulk-creation-protocol-handlers';
import { makeFriendlyUnits } from '../../../../shared/models/bacnet';
import { FileDownloadUtil } from '../../../../shared/utils/file-download.util';
import { Observable } from 'rxjs';

export interface CSVTemplateOptions {
  deviceId?: DeviceId;
  points?: BacnetPoint[] | ModbusPoint[] | LoRaWANPoint[];
  protocol: DeviceType;
}

/**
 * CSV Template Generator
 * Generates CSV templates for bulk tile creation
 */
export class CSVTemplateGenerator {
  static generateNumericTemplate(options: CSVTemplateOptions): any[] {
    return this.generateTemplate({
      ...options,
      tileType: TileFormat.Numeric,
      pointTypeFilter: [BacnetPointType.AnalogValue, BacnetPointType.AnalogInput, BacnetPointType.AnalogOutput]
    });
  }

  static generateToggleTemplate(options: CSVTemplateOptions): any[] {
    return this.generateTemplate({
      ...options,
      tileType: TileFormat.Toggle,
      pointTypeFilter: [BacnetPointType.BinaryValue, BacnetPointType.BinaryInput, BacnetPointType.BinaryOutput]
    });
  }

  static downloadCSV(data: any[], filename: string): Observable<void> {
    const csv = Papa.unparse(data);
    return FileDownloadUtil.downloadCsv(csv, filename);
  }

  private static generateTemplate(options: {
    deviceId?: DeviceId;
    points?: BacnetPoint[] | ModbusPoint[] | LoRaWANPoint[];
    protocol: DeviceType;
    tileType: TileFormat;
    pointTypeFilter: string[];
  }): any[] {
    const { deviceId, points, protocol, tileType, pointTypeFilter } = options;

    // Get template from config
    const config = PROTOCOL_CSV_CONFIG[protocol]?.[tileType];
    if (!config) {
      throw new Error(`No CSV configuration found for protocol: ${protocol}, tileType: ${tileType}`);
    }
    const template: any = {};
    config.columns.forEach(col => template[col] = '');
    const baseTemplate = { ...template, ...config.examples };

    if (!points || points.length === 0) {
      return [baseTemplate];
    }

    const rows = points
      .filter(point => this.shouldIncludePoint(point, pointTypeFilter, protocol, tileType))
      .map(point => this.createRowFromPoint(baseTemplate, deviceId, point, protocol, tileType));

    return rows.length > 0 ? rows : [baseTemplate];
  }

  private static shouldIncludePoint(
    point: BacnetPoint | ModbusPoint | LoRaWANPoint,
    typeFilter: string[],
    protocol: DeviceType,
    tileType: TileFormat
  ): boolean {
    if (protocol === DeviceType.BACnet && 'Type' in point) {
      return typeFilter.includes(point.Type);
    }

    if (protocol === DeviceType.Modbus && 'ID' in point && point.ID) {
      try {
        const configId = new ModbusPointID(point.ID);
        if (tileType === TileFormat.Numeric) {
          return [ModbusDataType.Int16, ModbusDataType.Int32, ModbusDataType.Float32, ModbusDataType.Float64].includes(configId.Type);
        } else {
          return configId.Type === ModbusDataType.Bool;
        }
      } catch {
        return false;
      }
    }

    if (protocol === DeviceType.LoRaWAN && 'Format' in point) {
      if (tileType === TileFormat.Numeric) {
        return point.Format === LoRaWANPointFormat.Numeric;
      } else {
        return point.Format === LoRaWANPointFormat.Boolean;
      }
    }

    return false;
  }

  private static createRowFromPoint(
    template: any,
    deviceId: DeviceId | undefined,
    point: BacnetPoint | ModbusPoint | LoRaWANPoint,
    protocol: DeviceType,
    tileType: TileFormat
  ): any {
    // Use protocol handler to generate tile name with fallback for empty names
    const protocolHandler = BULK_CREATION_PROTOCOL_HANDLERS[protocol];
    const tileName = protocolHandler.generateTileName(point);
    const row = { ...template, [CsvFieldId.MOTION_DEVICE_ID]: deviceId, [CsvFieldId.TILE_NAME]: tileName };

    if (protocol === DeviceType.BACnet && 'Address' in point) {
      const bacnetPoint = point as BacnetPoint;
      row[CsvFieldId.BACNET_INSTANCE_NUMBER] = bacnetPoint.Address;
      row[CsvFieldId.BACNET_OBJECT_TYPE] = bacnetPoint.Type;
      row[CsvFieldId.DISPLAY_NAME] = bacnetPoint.Description;
      row[CsvFieldId.ICON] = IconGuesserUtil.guessIcon(bacnetPoint);

      if (tileType === TileFormat.Numeric) {
        row[CsvFieldId.DATA_DISPLAY_UNITS] = typeof bacnetPoint.UnitsState === 'string' ? makeFriendlyUnits(bacnetPoint.UnitsState) : bacnetPoint.UnitsState;
      } else {
        // Match regular tile creation: use UnitsState if available as [false, true] array
        if (Array.isArray(bacnetPoint.UnitsState) && bacnetPoint.UnitsState.length === 2) {
          row[CsvFieldId.DATA_DISPLAY_FALSE_PHRASE] = bacnetPoint.UnitsState[0];
          row[CsvFieldId.DATA_DISPLAY_TRUE_PHRASE] = bacnetPoint.UnitsState[1];
        }
      }
    } else if (protocol === DeviceType.Modbus && 'ID' in point) {
      const configId = new ModbusPointID(point.ID);
      row[CsvFieldId.MODBUS_ADDRESS] = configId.Address.toString();
      row[CsvFieldId.MODBUS_DATA_TYPE] = configId.Type;
      row[CsvFieldId.MODBUS_BYTE_ORDER] = configId.Endianness;
      row[CsvFieldId.MODBUS_FUNCTIONS] = String(configId.Writable);

      row[CsvFieldId.DISPLAY_NAME] = tileName;
      row[CsvFieldId.ICON] = IconGuesserUtil.guessIconFromText(tileName);
    } else if (protocol === DeviceType.LoRaWAN && 'Name' in point) {
      const lorawanPoint = point as LoRaWANPoint;
      row[CsvFieldId.LORAWAN_POINT_NAME] = lorawanPoint.Name;
      row[CsvFieldId.LORAWAN_DATA_SOURCE] = lorawanPoint.Type;
      row[CsvFieldId.LORAWAN_DATA_TYPE] = lorawanPoint.Format;
      row[CsvFieldId.DISPLAY_NAME] = tileName;
      row[CsvFieldId.ICON] = IconGuesserUtil.guessIconFromText(tileName);
    }

    return row;
  }

}
